import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../components/Button';
import { useDispatch, useSelector } from 'react-redux';
import useAxiosPrivate from '../hooks/useAxiosPrivate';
import { toast } from 'react-toastify';
import { updateRegistrationData, setUserData } from '../store/userSlice';

function Step2({ setStep }) {
  const axiosPrivate = useAxiosPrivate();
  const dispatch = useDispatch();
  const { userId } = useSelector((state) => state.auth);
  const registrationData = useSelector((state) => state.user.registrationData);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [disabled, setDisabled] = useState(true);
  const [ghostPhoto, setGhostPhoto] = useState(null);
  const [ghostStyle, setGhostStyle] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  // Восстанавливаем фото из Redux при возврате на шаг
  const [photos, setPhotos] = useState(() => {
    if (!Array.isArray(registrationData.photos)) return {};

    return registrationData.photos.reduce((acc, photo, index) => {
      if (!photo) return acc;

      acc[index + 1] = typeof photo === 'string' ? { url: photo, isNew: false } : photo;

      return acc;
    }, {});
  });
  const fileInputs = useRef({});
  const justDragged = useRef(false);

  const checkPhotos = () => {
    const hasMainPhoto = Object.keys(photos).length > 0;
    setDisabled(!hasMainPhoto);
  };

  useEffect(() => {
    checkPhotos();
  }, [photos]);

  // Сохраняем фото в Redux, чтобы они не пропали при переходах между шагами
  useEffect(() => {
    const arr = [null, null, null, null];
    Object.entries(photos).forEach(([photoId, photo]) => {
      arr[Number(photoId) - 1] = photo;
    });
    dispatch(updateRegistrationData({ photos: arr }));
  }, [photos]);

  const handlePhotoSelect = (photoId) => {
    fileInputs.current[photoId]?.click();
  };

  const handleFileChange = (e, photoId) => {
    const file = e.target.files[0];
    if (file) {
      setPhotos((prev) => ({
        ...prev,
        [photoId]: {
          file: file,
          preview: URL.createObjectURL(file),
          isNew: true
        }
      }));
    }
  };

  const reorderPhotos = (fromPhotoId, toPhotoId) => {
    console.log(`Reordering photos: from ${fromPhotoId} to ${toPhotoId}`);
    setPhotos((prev) => {
      const newPhotos = { ...prev };

      // Получаем фото, которое перетаскиваем
      const draggedPhoto = newPhotos[fromPhotoId];
      const targetPhoto = newPhotos[toPhotoId];

      if (draggedPhoto && targetPhoto) {
        // Меняем местами
        console.log('Swapping photos:', draggedPhoto, 'with', targetPhoto);
        newPhotos[fromPhotoId] = targetPhoto;
        newPhotos[toPhotoId] = draggedPhoto;
      } else if (draggedPhoto && !targetPhoto) {
        // Перемещаем в пустое место
        console.log('Moving photo to empty slot:', draggedPhoto);
        newPhotos[toPhotoId] = draggedPhoto;
        delete newPhotos[fromPhotoId];
      }

      console.log('New photos state:', newPhotos);
      return newPhotos;
    });
  };

  const extractKeyFromUrl = (url) => {
    if (!url) return null;
    try {
      // Для presigned URLs и обычных S3 URLs
      if (url.includes('amazonaws.com/')) {
        const parts = url.split('amazonaws.com/');
        if (parts.length > 1) {
          // Убираем query параметры
          return decodeURIComponent(parts[1].split('?')[0]);
        }
      }
      // Fallback - пытаемся извлечь последнюю часть пути
      const pathname = new URL(url).pathname;
      return pathname.startsWith('/') ? pathname.substring(1) : pathname;
    } catch (e) {
      console.warn('Failed to extract key from URL:', url, e);
      return null;
    }
  };

  const handleNext = async () => {
    setIsLoading(true);
    try {
      // Сначала загружаем все новые фотографии
      const newPhotos = Object.entries(photos).filter(([photoId, photo]) => photo.isNew && photo.file);

      // Загружаем новые файлы и получаем их ключи
      const uploadedPhotoKeys = {};
      for (const [photoId, photoData] of newPhotos) {
        try {
          const formData = new FormData();
          formData.append('file', photoData.file);

          console.log('Uploading new photo...');
          const response = await axiosPrivate.post('/users/photo', formData, {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          });

          // Сохраняем ключ загруженной фотографии
          uploadedPhotoKeys[photoId] = response.data.data.key;
        } catch (error) {
          console.error('Failed to upload photo:', error);
          throw error;
        }
      }

      // Теперь создаем правильный порядок фотографий
      const orderedPhotoKeys = [];
      for (let i = 1; i <= 4; i++) {
        const photo = photos[i];
        if (photo) {
          if (photo.isNew) {
            // Новая фотография - используем загруженный ключ
            orderedPhotoKeys.push(uploadedPhotoKeys[i]);
          } else {
            // Существующая фотография - извлекаем ключ из URL
            const key = extractKeyFromUrl(photo.url);
            if (key) {
              orderedPhotoKeys.push(key);
            }
          }
        }
      }

      // Отправляем обновленный порядок фотографий на сервер
      if (orderedPhotoKeys.length > 0) {
        try {
          console.log('Sending new photos order to server:', orderedPhotoKeys);
          const orderResponse = await axiosPrivate.put('/users/photos-order', {
            photos: orderedPhotoKeys
          });
          console.log('Photos order updated successfully:', orderResponse.data);
        } catch (error) {
          // Если эндпоинт не существует, пропускаем ошибку
          console.warn('Failed to update photos order, endpoint may not exist:', error);
        }
      }

      // Перезагружаем данные пользователя, чтобы обновить порядок фотографий в Redux
      try {
        const updatedUserResponse = await axiosPrivate.get(`/users/${userId}`);
        dispatch(setUserData(updatedUserResponse.data));
      } catch (updateError) {
        console.warn('Failed to update user data in Redux:', updateError);
      }

      toast.success('Фотографии загружены!');

      // Переходим к следующему шагу
      setStep(3);
    } catch (error) {
      console.error('Ошибка загрузки фотографий:', error);
      toast.error('Ошибка при загрузке фотографий');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    justDragged.current = true;
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(index));
    document.body.classList.add('dragging');
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    document.body.classList.remove('dragging');
    setTimeout(() => {
      justDragged.current = false;
    }, 0);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex !== null && draggedIndex !== index) {
      e.currentTarget.classList.add('drag-over');
    }
  };

  const handleDragLeave = (e) => {
    e.currentTarget.classList.remove('drag-over');
  };

  const handleDrop = (e, targetPhotoId) => {
    e.preventDefault();
    e.currentTarget.classList.remove('drag-over');

    console.log('handleDrop', targetPhotoId);

    if (draggedIndex !== null && draggedIndex !== targetPhotoId) {
      reorderPhotos(draggedIndex, targetPhotoId);
    }
  };

  const handleDeletePhoto = (photoId) => {
    setPhotos((prev) => {
      const newPhotos = { ...prev };
      delete newPhotos[photoId];
      return newPhotos;
    });
  };

  // 💡 Touch Drag Support Below

  const handleTouchStart = (e, photoId) => {
    const photo = photos[photoId];
    if (!photo || (!photo.url && !photo.preview)) return;

    setDraggedIndex(photoId);
    setGhostPhoto(photo.url || photo.preview);

    e.preventDefault();
  };

  const handleTouchMove = (e) => {
    if (draggedIndex === null) return;

    const touch = e.touches[0];
    setGhostStyle({
      top: touch.clientY - 80 + 'px',
      left: touch.clientX - 80 + 'px',
      position: 'fixed',
      width: '160px',
      height: '160px',
      zIndex: 1000,
      pointerEvents: 'none',
      opacity: 0.8
    });

    e.preventDefault();
  };

  const handleTouchEnd = (e) => {
    if (draggedIndex === null) return;

    const touch = e.changedTouches[0];
    const dropTarget = document.elementFromPoint(touch.clientX, touch.clientY);
    const dropIndex = Number(dropTarget?.closest('[data-index]')?.getAttribute('data-index'));

    if (!isNaN(dropIndex) && dropIndex !== draggedIndex) {
      reorderPhotos(draggedIndex, dropIndex);
    }

    setDraggedIndex(null);
    setGhostPhoto(null);
  };

  return (
    <div className="flex flex-col items-center w-full overflow-y-auto overflow-x-hidden px-4">
      <h1 className="font-raleway font-semibold mt-6 text-white text-[20px]">Добавьте фото</h1>
      <p className="font-raleway font-light mt-2 text-white text-[16px]">Минимум одно, а лучше – все четыре</p>

      <div className="w-full mt-[24px] grid grid-cols-2 gap-[15px]">
        {[1, 2, 3, 4].map((photoId) => (
          <React.Fragment key={photoId}>
            <input
              type="file"
              ref={(el) => (fileInputs.current[photoId] = el)}
              onChange={(e) => handleFileChange(e, photoId)}
              accept="image/*"
              className="hidden"
            />
            <div
              key={photoId}
              data-index={photoId}
              role="button"
              tabIndex={0}
              aria-label={photos[photoId] ? `Фото ${photoId}${photoId === 1 ? ', главное' : ''}, нажать, чтобы заменить` : `Добавить фото ${photoId}`}
              draggable={!!photos[photoId]}
              onClick={() => {
                if (justDragged.current) return;
                handlePhotoSelect(photoId);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  if (justDragged.current) return;
                  handlePhotoSelect(photoId);
                }
              }}
              onDragStart={(e) => handleDragStart(e, photoId)}
              onDragEnd={handleDragEnd}
              onDragOver={(e) => handleDragOver(e, photoId)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, photoId)}
              onTouchStart={(e) => handleTouchStart(e, photoId)}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
              className={`w-full h-[164px] border-[1px] rounded-[16px] border-[#233636] bg-[#022424] relative flex items-center justify-center cursor-pointer overflow-hidden
              ${draggedIndex === photoId ? 'opacity-50' : ''}
              transition-all duration-200 ease-in-out
              hover:border-[#A1F69E]
              drag-over:border-[#A1F69E]
              drag-over:scale-105`}
            >
              {photoId === 1 && (
                <div className="absolute bottom-0 right-0 left-0 z-10 bg-gradient-to-t from-[#021A1A] to-transparent">
                  <p className="text-white font-raleway text-[14px] mx-auto text-center py-[10px] text-sx">
                    Главное фото
                  </p>
                </div>
              )}
              {photos[photoId]?.preview ? (
                <>
                  <img
                    src={photos[photoId].preview}
                    alt=""
                    aria-hidden="true"
                    className="w-full h-full object-cover rounded-[16px] select-none"
                  />
                  {photoId === 1 && (
                    <div className="absolute bottom-0 right-0 left-0 z-10 bg-gradient-to-t from-[#021A1A] to-transparent">
                      <p className="text-white font-raleway text-[14px] mx-auto text-center py-[10px] text-sx">
                        Главное фото
                      </p>
                    </div>
                  )}
                  <button
                    type="button"
                    aria-label={`Удалить фото ${photoId}`}
                    className="absolute top-[8px] right-[8px] pointer-events-auto z-10 cursor-pointer bg-transparent border-none p-0"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeletePhoto(photoId);
                    }}
                  >
                    <img src="/icons/delete_img.svg" className="w-[16px] h-[16px]" alt="" aria-hidden="true" />
                  </button>
                </>
              ) : photos[photoId]?.url ? (
                <>
                  <img
                    src={photos[photoId].url}
                    alt=""
                    aria-hidden="true"
                    className="w-full h-full object-cover rounded-[16px] select-none"
                  />
                  {photoId === 1 && (
                    <div className="absolute bottom-0 right-0 left-0 z-10 bg-gradient-to-t from-[#021A1A] to-transparent">
                      <p className="text-white font-raleway text-[14px] mx-auto text-center py-[10px] text-sx">
                        Главное фото
                      </p>
                    </div>
                  )}
                  <button
                    type="button"
                    aria-label={`Удалить фото ${photoId}`}
                    className="absolute top-[8px] right-[8px] pointer-events-auto z-10 cursor-pointer bg-transparent border-none p-0"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeletePhoto(photoId);
                    }}
                  >
                    <img src="/icons/delete_img.svg" className="w-[16px] h-[16px]" alt="" aria-hidden="true" />
                  </button>
                </>
              ) : (
                <>
                  <img alt="Камера" src="/icons/camera.svg" />
                </>
              )}
            </div>
          </React.Fragment>
        ))}
      </div>

      <p className="font-raleway font-light mt-[15px] text-white text-[16px]">Перетащите, чтобы изменить порядок</p>

      <div className="fixed w-full px-4 bottom-[20px]">
        <Button className='w-full' onClick={handleNext} disabled={disabled || isLoading}>
          {isLoading ? 'Сохранение...' : 'Далее'}
        </Button>
      </div>

      {/* 👻 Призрак фото для тача */}
      {ghostPhoto && (
        <img
          src={ghostPhoto}
          style={ghostStyle}
          className="rounded-[16px] shadow-lg transition-none"
          alt="Призрак фото"
        />
      )}
    </div>
  );
}

export default Step2;
